import { ErrorCode } from "@/types/api";

const TECHNICAL_ERROR_PATTERNS = [
	/invalid\s+`.*`\s+invocation/i,
	/prismaclient/i,
	/stack/i,
	/sql(state| syntax| error|ite| query)/i,
	/the table `.*` does not exist/i,
	/enoent/i,
	/ecconn|econn|socket hang up/i,
	/request failed with status code/i,
	/typeerror:/i,
	/syntaxerror:/i,
];

const FACE_VALIDATION_PATTERNS = [
	/人脸.{0,12}(校验|验证|检测|审核).{0,12}(失败|不通过|未通过|拒绝|拦截)/i,
	/(失败|不通过|未通过|拒绝|拦截).{0,12}人脸.{0,12}(校验|验证|检测|审核)/i,
	/(真人|人物|肖像).{0,12}(校验|验证|检测|审核).{0,12}(失败|不通过|未通过|拒绝|拦截)/i,
	/(face|portrait|identity|real person).{0,24}(check|verification|validation|detect|review|moderation|safety)/i,
	/(check|verification|validation|detect|review|moderation|safety).{0,24}(face|portrait|identity|real person)/i,
];

const FACE_VALIDATION_FRIENDLY_MESSAGE = "当前请求触发了人脸校验，建议更换人物图片或调整描述后重试";
const JIMENG_PERSONA_ASSET_SYNC_MESSAGE =
	"参考图片被上游人像审核拦截。请检查被拦素材的认证或入库状态；若为虚拟人物或疑似误伤，请复制排查信息交由支持人员复核。无需反复修改无关音频或提示词。";
const JIMENG_ASSET_SERVICE_MESSAGE =
	"即梦素材库服务未开通，暂时无法同步或使用人像驱动素材。请联系管理员开通后重试。";
const ACTIVE_JOB_CLEANUP_MESSAGE = "任务超过等待时间仍未完成，系统已自动结束。";
const PROVIDER_QUOTA_EXCEEDED_MESSAGE =
	"模型服务额度不足，平台正在处理，请稍后重试；你的账户余额不受影响";
const GENERIC_SERVER_ERROR_MESSAGE =
	"服务暂时不可用，请稍后重试；若持续失败，请联系管理员并提供错误ID";

const BACKEND_ERROR_MESSAGE_RULES: Array<{
	pattern: RegExp;
	message: string;
}> = [
	{
		pattern: /^script(?:\s+\S+)?\s+not\s+found$/i,
		message: "剧本不存在、已删除，或你没有访问权限",
	},
	{
		pattern: /额度不足|余额不足|insufficient[\s_]*(quota|credit|balance)|quota[\s_]*exceeded/i,
		message: PROVIDER_QUOTA_EXCEEDED_MESSAGE,
	},
	{
		pattern: /^access\s+denied$/i,
		message: "暂无权限访问该内容，请确认当前账号是否属于该项目组织",
	},
	{
		pattern: /^(character|scene|prop|storyboard)(?:\s+\S+)?\s+not\s+found$/i,
		message: "内容不存在、已删除，或你没有访问权限",
	},
];

const VOLCENGINE_PROVIDER_ERROR_MESSAGES: Record<string, string> = {
	"inputimagesensitivecontentdetected.privacyinformation": JIMENG_PERSONA_ASSET_SYNC_MESSAGE,
	inputimageriskdetection: "上传图片未通过安全校验，请更换人物图片后重试",
	inputtextriskdetection: "描述内容触发安全校验，请调整文案后重试",
	outputtextriskdetection: "生成结果触发安全校验，请调整描述或更换素材后重试",
	invalidparameter: "提交参数有误，请检查图片、尺寸或模型参数后重试",
	missingparameter: "请求缺少必要参数，请刷新页面后重试",
	"invalidendpoint.closedendpoint": "模型服务暂时不可用，请稍后重试",
};

const VOLCENGINE_PROVIDER_MESSAGE_RULES: Array<{
	pattern: RegExp;
	message: string;
}> = [
	{
		pattern:
			/(not activated the asset service|尚未开通.{0,20}asset service|未开通.{0,20}素材服务)/i,
		message: JIMENG_ASSET_SERVICE_MESSAGE,
	},
	{
		pattern: /(copyright restrictions?|版权限制|受版权保护|受限风格|风格限制)/i,
		message:
			"生成内容可能涉及版权或受限风格，请避免使用受版权保护的 IP、角色、艺术家或画风名称后重试",
	},
	{
		pattern: /(input image|输入图片).{0,80}(real person|privacy information|真人|隐私信息)/i,
		message: JIMENG_PERSONA_ASSET_SYNC_MESSAGE,
	},
	{
		pattern: /火山已拦截|先完成真人素材认证|输入图片疑似包含真人或隐私信息/i,
		message: JIMENG_PERSONA_ASSET_SYNC_MESSAGE,
	},
];

const SYSTEM_TASK_MESSAGE_RULES: Array<{
	pattern: RegExp;
	message: string;
}> = [
	{
		pattern: /Task expired while still marked active and was closed during active job cleanup/i,
		message: ACTIVE_JOB_CLEANUP_MESSAGE,
	},
	// 兜底：历史失败任务的原始报错（如"API error: 用户额度不足, 剩余额度: ¥-1.003252"）
	// 没有结构化分类，按文本识别；注意不匹配本地钱包文案"组织存在欠费"
	{
		pattern: /额度不足|余额不足|insufficient[\s_]*(quota|credit|balance)|quota[\s_]*exceeded/i,
		message: PROVIDER_QUOTA_EXCEEDED_MESSAGE,
	},
];

interface StructuredTaskErrorInput {
	message?: string;
	errorCategory?: string;
	rawMessage?: string;
	provider?: string;
	providerCode?: string;
	providerType?: string;
	providerStatusCode?: number;
	requestId?: string;
	providerTaskId?: string;
	retryable?: boolean;
}

type TaskErrorInput = string | StructuredTaskErrorInput | undefined;

export interface TaskErrorInfo {
	message: string;
	rawMessage?: string;
	matchedRule?:
		| "face_validation"
		| "provider_error_category"
		| "volcengine_provider_error"
		| "system_task_error";
}

function normalizeMessage(message: unknown): string | undefined {
	if (typeof message !== "string") return undefined;
	const trimmed = message.trim();
	if (!trimmed) return undefined;
	const firstLine = trimmed
		.split("\n")
		.map((line) => line.trim())
		.find(Boolean);
	if (!firstLine) return undefined;
	return firstLine.length > 180 ? `${firstLine.slice(0, 180)}...` : firstLine;
}

function isTechnicalMessage(message: string): boolean {
	return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function isGenericServerMessage(message: string): boolean {
	const normalized = message.toLowerCase();
	return (
		normalized === "internal server error" ||
		normalized === "internal error" ||
		normalized === "bad gateway" ||
		normalized === "service unavailable" ||
		normalized === "gateway timeout" ||
		normalized === "forbidden" ||
		normalized === "unauthorized" ||
		normalized === "not found" ||
		normalized === "bad request" ||
		normalized === "too many requests"
	);
}

function matchesFaceValidationMessage(message: string): boolean {
	return FACE_VALIDATION_PATTERNS.some((pattern) => pattern.test(message));
}

function mapKnownBackendMessage(message: string): string | undefined {
	return BACKEND_ERROR_MESSAGE_RULES.find((rule) => rule.pattern.test(message))?.message;
}

function normalizeProviderKey(value: unknown): string | undefined {
	if (typeof value !== "string") return undefined;
	const normalized = value.trim().toLowerCase();
	return normalized || undefined;
}

function resolveVolcengineProviderMessage(error: StructuredTaskErrorInput): string | undefined {
	const provider = normalizeProviderKey(error.provider);
	if (provider && provider !== "volcengine") {
		return undefined;
	}

	const candidateMessages = [error.rawMessage, error.message]
		.map((value) => normalizeMessage(value))
		.filter((value): value is string => !!value);
	const highPriorityMatchedRule = VOLCENGINE_PROVIDER_MESSAGE_RULES.find((rule) =>
		candidateMessages.some((candidateMessage) => rule.pattern.test(candidateMessage)),
	);
	if (highPriorityMatchedRule) {
		return highPriorityMatchedRule.message;
	}

	const exactCode = normalizeProviderKey(error.providerCode);
	if (exactCode && VOLCENGINE_PROVIDER_ERROR_MESSAGES[exactCode])
		return VOLCENGINE_PROVIDER_ERROR_MESSAGES[exactCode];
	const providerType = normalizeProviderKey(error.providerType);
	if (providerType && VOLCENGINE_PROVIDER_ERROR_MESSAGES[providerType]) {
		return VOLCENGINE_PROVIDER_ERROR_MESSAGES[providerType];
	}

	const providerCode = normalizeProviderKey(error.providerCode);
	if (providerCode && VOLCENGINE_PROVIDER_ERROR_MESSAGES[providerCode]) {
		return VOLCENGINE_PROVIDER_ERROR_MESSAGES[providerCode];
	}

	return undefined;
}

function resolveProviderCategoryMessage(error: StructuredTaskErrorInput): string | undefined {
	switch (error.errorCategory) {
		case "PROVIDER_NO_CHANNEL":
			return "当前模型渠道不可用，请稍后重试或切换模型";
		case "PROVIDER_RATE_LIMITED":
			return "上游平台请求过于频繁，请稍后重试";
		case "PROVIDER_TIMEOUT":
			return "上游平台响应超时，请稍后重试";
		case "PROVIDER_CONTENT_POLICY":
			return "请求内容未通过上游安全校验，请调整描述或更换素材后重试";
		case "PROVIDER_AUTH_FAILED":
			return "上游平台鉴权失败，请联系管理员检查模型渠道配置";
		case "PROVIDER_QUOTA_EXCEEDED":
			return PROVIDER_QUOTA_EXCEEDED_MESSAGE;
		case "PROVIDER_BAD_REQUEST":
			return "上游平台拒绝了请求参数，请检查图片、尺寸或模型参数后重试";
		case "PROVIDER_UNAVAILABLE":
			return "上游平台暂时不可用，请稍后重试或切换模型";
		case "SYNC_FAILED":
			return "生成成功，但保存到业务数据失败，请重试同步";
		default:
			return undefined;
	}
}

function extractErrorCode(error: unknown): number | undefined {
	if (!error || typeof error !== "object") return undefined;
	const candidate = error as {
		code?: unknown;
		response?: { data?: { code?: unknown } };
		details?: { code?: unknown };
	};
	const raw = candidate.code ?? candidate.response?.data?.code ?? candidate.details?.code;
	if (typeof raw === "number" && Number.isFinite(raw)) {
		return raw;
	}
	return undefined;
}

export function extractErrorStatusCode(error: unknown): number | undefined {
	if (!error || typeof error !== "object") return undefined;
	const candidate = error as {
		status?: unknown;
		response?: { status?: unknown };
	};
	const raw = candidate.response?.status ?? candidate.status;
	if (typeof raw === "number" && Number.isFinite(raw)) {
		return raw;
	}
	return undefined;
}

function extractRawErrorMessage(error: unknown): string | undefined {
	if (typeof error === "string") return normalizeMessage(error);
	if (!error || typeof error !== "object") return undefined;

	const candidate = error as {
		message?: unknown;
		error?: { message?: unknown };
		response?: { data?: { message?: unknown; error?: { message?: unknown } } };
		details?: { message?: unknown; error?: { message?: unknown } };
	};

	return (
		normalizeMessage(candidate.message) ||
		normalizeMessage(candidate.error?.message) ||
		normalizeMessage(candidate.response?.data?.message) ||
		normalizeMessage(candidate.response?.data?.error?.message) ||
		normalizeMessage(candidate.details?.message) ||
		normalizeMessage(candidate.details?.error?.message)
	);
}

function mapCodeToMessage(code: number | undefined): string | undefined {
	switch (code) {
		case ErrorCode.UNAUTHORIZED:
			return "登录已过期，请重新登录";
		case ErrorCode.FORBIDDEN:
			return "暂无权限执行该操作";
		case ErrorCode.NOT_FOUND:
			return "请求资源不存在或已被删除";
		case ErrorCode.RATE_LIMITED:
			return "请求过于频繁，请稍后再试";
		case ErrorCode.INTERNAL_SERVER_ERROR:
			return GENERIC_SERVER_ERROR_MESSAGE;
		default:
			return undefined;
	}
}

function mapStatusToMessage(status: number | undefined): string | undefined {
	if (!status) return undefined;
	switch (status) {
		case 400:
			return "请求参数有误，请检查后重试";
		case 401:
			return "登录已过期，请重新登录";
		case 403:
			return "暂无权限执行该操作";
		case 404:
			return "请求资源不存在或已被删除";
		case 408:
			return "请求超时，请稍后重试";
		case 409:
			return "数据冲突，请刷新后重试";
		case 413:
			return "上传内容过大，请压缩后重试";
		case 415:
			return "文件格式不支持，请更换后重试";
		case 422:
			return "提交内容不符合要求，请检查后重试";
		case 429:
			return "请求过于频繁，请稍后再试";
		default:
			if (status >= 500) {
				return GENERIC_SERVER_ERROR_MESSAGE;
			}
			return undefined;
	}
}

export function resolveErrorMessage(error: unknown, fallback = "请求失败"): string {
	const rawMessage = extractRawErrorMessage(error);
	const backendMessage = rawMessage ? mapKnownBackendMessage(rawMessage) : undefined;
	if (backendMessage) return backendMessage;

	if (rawMessage && !isTechnicalMessage(rawMessage) && !isGenericServerMessage(rawMessage)) {
		return rawMessage;
	}

	const codeMessage = mapCodeToMessage(extractErrorCode(error));
	if (codeMessage) return codeMessage;

	const statusMessage = mapStatusToMessage(extractErrorStatusCode(error));
	if (statusMessage) return statusMessage;

	return fallback;
}

export function formatActionErrorMessage(
	action: string,
	error: unknown,
	fallback = `${action}失败，请稍后重试`,
): string {
	const reason = resolveErrorMessage(error, fallback);
	if (reason.includes(action)) {
		return reason;
	}
	return `${action}失败：${reason}`;
}

export function resolveTaskErrorMessage(
	taskError: TaskErrorInput,
	fallback = "任务失败，请稍后重试",
): string {
	return resolveTaskErrorInfo(taskError, fallback).message;
}

export function resolveTaskErrorInfo(
	taskError: TaskErrorInput,
	fallback = "任务失败，请稍后重试",
): TaskErrorInfo {
	const normalizedError = typeof taskError === "string" ? { message: taskError } : taskError;
	const normalizedRawMessage = normalizeMessage(normalizedError?.rawMessage);
	const normalizedMessage = normalizeMessage(normalizedError?.message);
	const providerCategoryMessage = normalizedError
		? resolveProviderCategoryMessage(normalizedError)
		: undefined;

	const providerMessage = normalizedError
		? resolveVolcengineProviderMessage(normalizedError)
		: undefined;
	if (
		providerMessage &&
		(!providerCategoryMessage ||
			normalizedError?.providerCode?.toLowerCase() ===
				"inputimagesensitivecontentdetected.privacyinformation")
	) {
		return {
			message: providerMessage,
			rawMessage: normalizedRawMessage || normalizedMessage,
			matchedRule: "volcengine_provider_error",
		};
	}
	if (providerCategoryMessage) {
		return {
			message: providerCategoryMessage,
			rawMessage: normalizedRawMessage || normalizedMessage,
			matchedRule: "provider_error_category",
		};
	}

	const normalized = normalizedRawMessage || normalizedMessage;
	if (!normalized) return { message: fallback };
	const systemTaskRule = SYSTEM_TASK_MESSAGE_RULES.find((rule) => rule.pattern.test(normalized));
	if (systemTaskRule) {
		return {
			message: systemTaskRule.message,
			rawMessage: normalized,
			matchedRule: "system_task_error",
		};
	}
	if (matchesFaceValidationMessage(normalized)) {
		return {
			message: FACE_VALIDATION_FRIENDLY_MESSAGE,
			rawMessage: normalized,
			matchedRule: "face_validation",
		};
	}
	if (isTechnicalMessage(normalized) || isGenericServerMessage(normalized)) {
		return {
			message: fallback,
			rawMessage: normalized,
		};
	}
	return {
		message: normalized,
		rawMessage: normalized,
	};
}
